<script setup lang="ts">
import {computed} from 'vue'
import {useI18n} from 'vue-i18n'
import {useNewUiEnabled} from '@/composable/useNewUiEnabled'
import ErrorMessage from './form/ErrorMessage.vue'

export interface Props {
  id?: string
  name?: string
  label?: string
  placeholder?: string
  inputMode?: 'none' | 'text' | 'decimal' | 'numeric' | 'tel' | 'search' | 'email' | 'url'
  maxLength?: number
  pattern?: string
  readOnly?: boolean
  min?: number
  max?: number
  slim?: boolean
  variant?: string
  disabled?: boolean
  autoFocus?: boolean
  customStyle?: string
  error?: boolean
  errorMessage?: string
  modelValue?: string
}

const props = withDefaults(defineProps<Props>(), {slim: false, variant: 'text'})
const emit = defineEmits(['update:modelValue', 'valueChanged', 'inputEvent', 'onEnter'])
const isNewUiEnabled = useNewUiEnabled()
const {t} = useI18n()

const hasError = computed(() => props.error || !!props.errorMessage)
const showNewUiNumberControls = computed(() => isNewUiEnabled.value && props.variant === 'number')
const currentNumberValue = computed(() => Number.parseFloat(props.modelValue ?? ''))
const canIncreaseNumber = computed(
  () =>
    !props.disabled &&
    (props.max === undefined || !Number.isFinite(currentNumberValue.value) || currentNumberValue.value < props.max)
)
const canDecreaseNumber = computed(
  () =>
    !props.disabled &&
    (props.min === undefined || !Number.isFinite(currentNumberValue.value) || currentNumberValue.value > props.min)
)

function emitValue(value: string) {
  emit('update:modelValue', value)
  emit('valueChanged', value)
}

const onInput = function (event: Event) {
  const target = event.target as HTMLInputElement
  emitValue(target.value)
  emit('inputEvent', event)
}

function getNumberBaseValue(direction: number) {
  if (Number.isFinite(currentNumberValue.value)) {
    return currentNumberValue.value
  }

  if (direction > 0 && props.min !== undefined) {
    return props.min - 1
  }

  if (direction < 0 && props.max !== undefined) {
    return props.max + 1
  }

  return 0
}

function clampNumberValue(value: number) {
  if (props.min !== undefined && value < props.min) {
    return props.min
  }

  if (props.max !== undefined && value > props.max) {
    return props.max
  }

  return value
}

function updateNumberValue(direction: number) {
  if (props.disabled) {
    return
  }

  emitValue(clampNumberValue(getNumberBaseValue(direction) + direction).toString())
}
</script>

<template>
  <label
    v-if="label"
    :class="['block', isNewUiEnabled ? 'mb-new-sm text-new-base text-new-neutral-700' : 'mb-2 font-bold text-branddark text-sm']"
    :for="id"
  >
    {{ label }}
  </label>
  <div
    v-if="showNewUiNumberControls"
    :class="{
      'group relative flex w-full items-center overflow-hidden rounded-lg bg-new-neutral-50': true,
      'h-8': !props.slim,
      '!h-7': props.slim,
      'cursor-not-allowed': disabled,
      'focus-within:outline focus-within:outline-1 focus-within:outline-offset-2 focus-within:outline-new-primary-500':
        isNewUiEnabled && !disabled,
      '!border !border-new-red-500': hasError && isNewUiEnabled,
      [props.customStyle ?? '']: !!props.customStyle,
    }"
  >
    <input
      :id="id"
      :autofocus="autoFocus"
      class="min-w-0 flex-1 !border-none !bg-transparent px-3 py-0 pr-8 text-base leading-none text-new-neutral-700 shadow-none outline-none transition-colors [appearance:textfield] hover:!bg-transparent focus:!bg-transparent focus:ring-0 [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none"
      :disabled="disabled"
      :inputmode="inputMode"
      :max="max"
      :maxlength="maxLength"
      :min="min"
      :name="name"
      :pattern="pattern"
      :placeholder="placeholder"
      :readonly="readOnly"
      :type="variant"
      :value="modelValue"
      @input="onInput"
      @keydown.enter="$emit('onEnter')"
    />
    <div
      class="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 flex-col items-center opacity-0 transition-opacity group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100"
    >
      <button
        :aria-label="t('action.increase')"
        class="flex h-3.5 w-5 items-end justify-center text-new-secondary-500 transition-colors hover:text-new-secondary-600 disabled:text-new-neutral-300"
        :disabled="!canIncreaseNumber"
        type="button"
        @click="updateNumberValue(1)"
      >
        <i class="fa-solid fa-chevron-up text-sm leading-none"></i>
      </button>
      <button
        :aria-label="t('action.decrease')"
        class="flex h-3.5 w-5 items-start justify-center text-new-secondary-500 transition-colors hover:text-new-secondary-600 disabled:text-new-neutral-300"
        :disabled="!canDecreaseNumber"
        type="button"
        @click="updateNumberValue(-1)"
      >
        <i class="fa-solid fa-chevron-down text-sm leading-none"></i>
      </button>
    </div>
  </div>
  <input
    v-else
    :id="id"
    :autofocus="autoFocus"
    :class="{
      'w-full shadow-none appearance-none py-0 text-base transition-colors leading-none': true,
      'h-11 px-5 border-2 border-solid border-brandlightdarker rounded-none text-branddark bg-brandverylight hover:border-brandmain hover:text-branddarkdarker focus:border-brandmain focus:text-branddarkdarker focus:outline-none':
        !isNewUiEnabled,
      '!rounded-lg h-8 px-3 py-2.5 !bg-new-neutral-50': isNewUiEnabled,
      'focus-within:outline focus-within:outline-1 focus-within:outline-offset-2 focus-within:outline-new-primary-500':
        isNewUiEnabled && !disabled,
      '!text-new-neutral-400 cursor-not-allowed': disabled && isNewUiEnabled,
      '!text-new-neutral-700': !disabled && isNewUiEnabled,
      '!h-7': props.slim,
      '!border !border-new-red-500': hasError && isNewUiEnabled,
      '!border-none': !hasError && isNewUiEnabled,
      [props.customStyle ?? '']: !!props.customStyle,
    }"
    :disabled="disabled"
    :inputmode="inputMode"
    :max="max"
    :maxlength="maxLength"
    :min="min"
    :name="name"
    :pattern="pattern"
    :placeholder="placeholder"
    :readonly="readOnly"
    :type="variant"
    :value="modelValue"
    @input="onInput"
    @keydown.enter="$emit('onEnter')"
  />
  <ErrorMessage v-if="errorMessage" :message="errorMessage" />
</template>

<script></script>
